import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import { getCalendar } from 'flow/actions/extension-actions';
import ContactScheduleEvents from 'flow/components/contact-card/ContactScheduleEvents';
import Icon from 'flow/components/ui-components/Icon';
import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { OpeningHour } from 'flow/types/Calendar';
import { TypedMap } from 'flow/types/TypedMap';
import { getFilteredCalendarEvents } from 'flow/utils/calendar-utils';
import { generatePath } from 'flow/utils/router-utils';
import CentralSwitchEmptyState from '../CentralSwitchEmptyState';
import CentralSwitchHeader from '../CentralSwitchHeader';
import { routes } from 'flow/types/routes';
import IconButton from 'flow/components/ui-components/IconButton';

const UserCalendarCentralSwitchWidget = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { pathname } = useLocation();

    const { ownExtension } = useExtension();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const calendar = ownExtension.get('calendar');

    useEffect(() => {
        dispatch(getCalendar(ownExtensionKey));
    }, [dispatch, ownExtensionKey]);

    if (!calendar) {
        return null;
    }

    const startInterval = moment().startOf('day');
    const endInterval = moment().endOf('day');
    const events: TypedMap<OpeningHour> = getFilteredCalendarEvents(
        calendar.get('events'),
        startInterval,
        endInterval,
    );
    const currentTime = moment();
    const futureEvents = events.filter(
        (calendarEvent) => moment(calendarEvent.get('end')) > currentTime,
    );

    return (
        <>
            <CentralSwitchHeader>
                <div>{i18next.t('contact.calendar')}</div>
                <IconButton
                    aria-label={i18next.t('settings.workhours')}
                    className="group rounded-sm p-1 hover:bg-gray-100"
                    onClick={() =>
                        history.push(
                            generatePath(routes['settings.work-hours'], {
                                extensionKey: ownExtensionKey,
                            }),
                            {
                                from: pathname,
                            },
                        )
                    }
                >
                    <Icon
                        className="text-gray-400 group-hover:text-gray-500 rtl:scale-x-[-1]"
                        iconKey="arrow_forward"
                    />
                </IconButton>
            </CentralSwitchHeader>
            <main>
                {futureEvents.size === 0 ? (
                    <CentralSwitchEmptyState uniqueId="UserSchedule" />
                ) : (
                    <div className="px-4">
                        <ContactScheduleEvents calendar={calendar} />
                    </div>
                )}
            </main>
        </>
    );
};

export default UserCalendarCentralSwitchWidget;
